import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense, forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { SearchField } from '@sas/nova-commons/components/SearchField';
import { renderInlineCssVars, useDesignTokens } from '@sas/nova-commons/theme';
import { useResizeObserverRef } from '@sas/nova-commons/hooks/useResizeObserver';
import { LocationPopover } from './LocationPopover';
import { useI18n } from '../../../../locale/I18nContext';
import { useLatestRef, useRenderEffect } from '../../../hooks';
import { Label } from '@sas/nova-commons/components/Label';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { withLabels } from '@sas/nova-commons/components/withLabels';
import useOnFocusOut from '@sas/nova-commons/hooks/useOnFocusOut';
import { connect } from 'react-redux';
import { selectObjectSearchState, selectVisualizationState } from '../../../../store/selectors';
import { Item, List } from '@sas/nova-commons/components/List';
import { useListState } from '@sas/nova-commons/components/List/useListState';
import { PanelListItem, panelItemHeight } from './PanelListItem';
import { Popover } from '@sas/nova-commons/components/Popover';
import { useRefArray } from '@sas/nova-commons/hooks/useRefArray';
import './PanelSearchField.css';
import cn from './PanelSearchField.css.json';
const SearchFieldWithLabels = withLabels(SearchField);
export const PanelSearchFieldBase = forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e, _f;
    const localize = useI18n();
    const dt = useDesignTokens();
    const tokens = renderInlineCssVars({
        bg: dt.reportTokens.backgrounds.panes.bg,
        bd: dt.reportTokens.backgroundsDepth.standard.bd,
    }, 'panelSearchField');
    const listState = useListState();
    const hintRefs = useRefArray();
    const [searchFieldWidth, setSearchFieldWidth] = useState(undefined);
    const [setSearchFieldRef] = useResizeObserverRef((entry) => {
        setSearchFieldWidth(entry.target.getBoundingClientRect().width);
    });
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => ({
        focus: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
    }), []);
    const [hasFocus, setHasFocus] = useState(false);
    const [inputHasFocus, setInputHasFocus] = useState(false);
    const searchFieldFocusHandlers = useOnFocusOut(() => {
        var _a;
        if (!props.contentId ||
            ((_a = document.getElementById(props.contentId)) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
            // Ignore clicks on the canvas
            return;
        }
        setHasFocus(false);
    });
    const hasLocation = !!((_a = props.feature) === null || _a === void 0 ? void 0 : _a.location);
    const hasCandidate = !!props.locationCandidate;
    const [userValue, setUserValue] = useState('');
    // Reset userValue when a location is added
    let willRerender = useRenderEffect(() => {
        if (hasLocation) {
            setUserValue('');
        }
    }, [hasLocation]);
    // Focus the input on first render if empty
    {
        const refFirstRender = useRef(true);
        useEffect(() => {
            var _a;
            if (refFirstRender.current && !hasLocation && !props.disableAutoFocus) {
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
            refFirstRender.current = false;
        }, [hasLocation, props.disableAutoFocus]);
    }
    // Notify the parent to update pin mode. We can't control pin mode directly,
    // since RoutePanel supports multiple PanelSearchFields
    {
        const refOnUpdatePinMode = useLatestRef(props.onUpdatePinMode);
        const needsPinMode = hasFocus && !hasLocation && !userValue;
        useEffect(() => {
            refOnUpdatePinMode.current(needsPinMode);
        }, [refOnUpdatePinMode, needsPinMode]);
    }
    {
        // Clear hints when a candidate is added
        const refOnUpdateHints = useLatestRef(props.onUpdateHints);
        useEffect(() => {
            if (hasCandidate) {
                refOnUpdateHints.current({ searchTerm: '' });
            }
        }, [refOnUpdateHints, hasCandidate]);
    }
    // Reset selected row when the value changes
    willRerender =
        useRenderEffect(() => {
            listState.setSelectRows({ selectedRows: new Set() });
        }, [userValue]) || willRerender;
    if (willRerender) {
        // bail for performance
        return null;
    }
    const pins = (_b = props.features) === null || _b === void 0 ? void 0 : _b.filter((feature) => feature.type === 'pin');
    const results = props.includePinResults && userValue
        ? [
            ...((_c = props.searchHints) !== null && _c !== void 0 ? _c : []),
            ...((pins === null || pins === void 0 ? void 0 : pins.filter((pin) => {
                var _a;
                return (pin.name.toLocaleLowerCase().includes(userValue.toLocaleLowerCase()) ||
                    ((_a = pin.location) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase().includes(userValue.toLocaleLowerCase())));
            })) || []),
        ]
        : (_d = props.searchHints) !== null && _d !== void 0 ? _d : [];
    const popoverIsOpen = !!userValue && !props.locationCandidate && !!results.length && hasFocus;
    const [selectedRowKey = '-1'] = (_e = listState.preference.selectedRows) !== null && _e !== void 0 ? _e : [];
    const selectedRow = parseInt(selectedRowKey);
    const selectedHint = results[selectedRow];
    return (_jsxs(Flow, Object.assign({ orientation: "vertical", spacing: "s", ref: (e) => {
            var _a;
            setSearchFieldRef((_a = e === null || e === void 0 ? void 0 : e.lastElementChild) !== null && _a !== void 0 ? _a : null);
        }, style: tokens, onFocus: (e) => {
            setHasFocus(true);
            searchFieldFocusHandlers.onFocus();
            // inputRef.current can be null here starting with NC v32
            // Hack: checking tagName instead
            if ( /* e.target === inputRef.current */e.target.tagName === 'INPUT') {
                setInputHasFocus(true);
            }
        }, onBlur: (e) => {
            if ( /* e.target === inputRef.current */e.target.tagName === 'INPUT') {
                setInputHasFocus(false);
            }
            searchFieldFocusHandlers.onBlur();
        }, onKeyDown: (e) => {
            var _a, _b;
            if (e.target !== inputRef.current) {
                return;
            }
            switch (e.key) {
                case 'ArrowDown':
                    const nextRow = selectedRow === -1 ? 0 : selectedRow + 1;
                    const hasNext = nextRow < results.length;
                    listState.setSelectRows({
                        selectedRows: new Set(hasNext ? [`${nextRow}`] : []),
                    });
                    (_a = hintRefs.refs[hasNext ? nextRow : 0]) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: 'nearest' });
                    e.preventDefault();
                    break;
                case 'ArrowUp':
                    const prevRow = selectedRow === -1 ? results.length - 1 : selectedRow - 1;
                    const hasPrev = prevRow >= 0;
                    listState.setSelectRows({
                        selectedRows: new Set(hasPrev ? [`${prevRow}`] : []),
                    });
                    (_b = hintRefs.refs[hasPrev ? prevRow : 0]) === null || _b === void 0 ? void 0 : _b.scrollIntoView({ block: 'nearest' });
                    e.preventDefault();
                    break;
            }
        } }, { children: [props.label && (
            // SearchField doesn't work with ariaLabelledBy, so we have to render the label manually
            _jsx(Suspense, Object.assign({ fallback: null }, { children: _jsx(Label, Object.assign({ helpPopup: props.labelHelp }, { children: props.label })) }))), _jsx(SearchFieldWithLabels, { ariaLabel: (_f = props.ariaLabel) !== null && _f !== void 0 ? _f : props.label, fieldWidth: "100%", className: cn.panelSearchField, onChange: (e) => {
                    setUserValue(e.value);
                    props.onInvalidateLocation();
                    props.onUpdateHints({ searchTerm: e.value });
                }, onCommit: (e) => {
                    var _a;
                    if (!e.value) {
                        setUserValue('');
                        return;
                    }
                    switch (selectedHint === null || selectedHint === void 0 ? void 0 : selectedHint.type) {
                        case 'pin':
                            (_a = props.onApplyPin) === null || _a === void 0 ? void 0 : _a.call(props, {
                                pin: selectedHint,
                            });
                            break;
                        case 'hint':
                            props.onApplyHint({
                                hint: selectedHint,
                            });
                            break;
                        default:
                            props.onApplyHint({
                                searchTerm: e.value,
                            });
                    }
                }, value: (selectedHint === null || selectedHint === void 0 ? void 0 : selectedHint['location']) ||
                    (selectedHint === null || selectedHint === void 0 ? void 0 : selectedHint.name) ||
                    userValue ||
                    props.feature.location ||
                    '', placeholder: localize({
                    key: inputHasFocus && !props.locationCandidate
                        ? 'PinPanel.searchClickPlaceholder.txt'
                        : 'PinPanel.searchPlaceholder.txt',
                }), inputRef: inputRef, clearButtonTooltip: localize({
                    key: 'PinPanel.clearLocation.tooltip.txt',
                }), numberOfResults: userValue ? results.length : null }), _jsx(Popover, Object.assign({ isOpen: popoverIsOpen, referenceElementRef: inputRef, hideArrow: true, placement: 'bottom-start', isModal: false, focusLockProps: { disabled: true }, onClose: () => {
                    props.onUpdateHints({ searchTerm: '' });
                }, style: {
                    width: searchFieldWidth,
                    padding: 0,
                    marginLeft: -1,
                    overflow: 'auto',
                    cursor: 'auto',
                } }, { children: _jsx(List, Object.assign({}, listState, { rowRef: (index) => hintRefs.createSetRef(index), selectionMode: "single", rowCount: results.length, showSeparator: () => false, width: (searchFieldWidth || 2) - 2, itemHeight: () => 'double', height: panelItemHeight * (results.length || 2), getItemInfo: (index) => {
                        return {
                            type: Item,
                            props: {
                                itemID: '' + index,
                                children: undefined,
                            },
                        };
                    }, renderItem: ({ index }) => {
                        const item = results[index];
                        return (_jsx(PanelListItem, { item: item, icon: item.type === 'hint' ? props.hintIconOverride : undefined }));
                    }, style: { overflow: 'hidden' }, onSelect: (rows) => {
                        var _a;
                        const [selectedRow = '-1'] = rows || [];
                        const selectedValue = results[parseInt(selectedRow)];
                        switch (selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.type) {
                            case 'pin':
                                (_a = props.onApplyPin) === null || _a === void 0 ? void 0 : _a.call(props, {
                                    pin: selectedValue,
                                });
                                break;
                            case 'hint':
                                props.onApplyHint({
                                    hint: selectedValue,
                                });
                                break;
                        }
                    } })) })), _jsx(LocationPopover, { id: props.id, contentId: props.contentId, title: props.locationPopoverTitle, locationCandidate: props.locationCandidate, onOutsideClick: () => {
                    searchFieldFocusHandlers.onBlur();
                    props.onOutsideClick();
                }, onConfirm: () => {
                    setHasFocus(false);
                    props.onConfirm();
                }, onCancel: () => {
                    setHasFocus(false);
                    props.onCancel();
                } })] })));
});
export const PanelSearchField = connect((state, props) => {
    var _a, _b;
    const { contentId } = (_a = selectVisualizationState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    const { features, searchHints } = (_b = selectObjectSearchState(state, props.id)) !== null && _b !== void 0 ? _b : {};
    return { contentId, features, searchHints };
}, undefined, undefined, { forwardRef: true })(PanelSearchFieldBase);
